import { forwardRef, useContext, type ComponentPropsWithoutRef } from "react";
import Arrow_right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import { DetailsContext } from "./DetailsContext";
import Icon from "../../Icon";

import cx from "classnames";
import styles from "../Details.module.scss";

/**
 * Props for the DetailsArrow component
 * @extends ComponentPropsWithoutRef<"span">
 */
export type DetailsArrowProps = ComponentPropsWithoutRef<"span">;

/**
 * DetailsArrow component for the animated arrow indicator in DetailsSummary.
 *
 * Features:
 * - Animated chevron icon that rotates on expand/collapse
 * - Automatically reflects the current expanded state
 * - Uses consistent icon sizing and styling
 * - Smooth transition animations
 * - Accessible with proper ARIA context
 *
 * @example
 * <DetailsArrow className="custom-arrow" />
 */
export const DetailsArrow = forwardRef<HTMLSpanElement, DetailsArrowProps>(
  function DetailsArrow({ className: classNameProp, ...rest }, ref) {
    const { isExpanded } = useContext(DetailsContext);

    const className = cx(
      styles.arrow,
      { [styles["is-expanded"]]: isExpanded },
      classNameProp,
    );

    return (
      <span className={className} ref={ref} {...rest}>
        <Icon size="large" svg={Arrow_right} />
      </span>
    );
  },
);
